/**
 * HFDS — Headzflex Design System
 * Component: HF-003 Variant Pills
 * Version: 1.0.0
 *
 * Responsibilities:
 * - Read Shopify variant data.
 * - Resolve the selected variant.
 * - Synchronize product form variant ID.
 * - Update option availability.
 * - Update the variant URL.
 * - Emit reusable HFDS events.
 */

(() => {
  "use strict";

  const ROOT_SELECTOR = "[data-hf-variant-picker]";
  const INPUT_SELECTOR = "[data-hf-option-input]";
  const PILL_SELECTOR = "[data-hf-option-pill]";
  const JSON_SELECTOR = "[data-hf-variants-json]";
  const STATUS_SELECTOR = "[data-hf-variant-status]";
  const SELECTION_SELECTOR = "[data-hf-option-selection]";

  class HFVariantPicker {
    /**
     * @param {HTMLElement} root
     */
    constructor(root) {
      this.root = root;
      this.productId = String(root.dataset.productId || "");
      this.sectionId = String(root.dataset.sectionId || "");
      this.formId = String(root.dataset.productFormId || "");
      this.updateUrl = root.dataset.updateUrl !== "false";
      this.disableImpossible =
        root.dataset.disableImpossible === "true";

      this.variants = [];
      this.optionNames = [];
      this.currentVariant = null;

      this.handleChange = this.handleChange.bind(this);
      this.handleFormSubmit = this.handleFormSubmit.bind(this);

      this.initialize();
    }

    initialize() {
      if (!window.HFDS) {
        console.error(
          "[HFDS Variant Picker] HFDS core is unavailable."
        );
        return;
      }

      const jsonElement = window.HFDS.query(
        JSON_SELECTOR,
        this.root
      );

      this.variants = window.HFDS.getJSONScript(
        jsonElement,
        []
      );

      this.optionNames = window.HFDS.queryAll(
        "[data-hf-option-group]",
        this.root
      ).map((group) => group.dataset.optionName || "");

      if (!Array.isArray(this.variants) || this.variants.length === 0) {
        this.setError(
          "Variant information could not be loaded."
        );
        return;
      }

      this.root.addEventListener("change", this.handleChange);

      const form = this.getProductForm();

      if (form) {
        form.addEventListener(
          "submit",
          this.handleFormSubmit
        );
      }

      this.resolveVariant({
        updateUrl: false,
        emitEvent: false,
        announce: false
      });

      this.root.dataset.hfInitialized = "true";
    }

    /**
     * Returns all option inputs.
     *
     * @returns {HTMLInputElement[]}
     */
    getInputs() {
      return window.HFDS
        .queryAll(INPUT_SELECTOR, this.root)
        .filter((element) => element instanceof HTMLInputElement);
    }

    /**
     * Returns checked option values in option order.
     *
     * @returns {string[]}
     */
    getSelectedOptions() {
      return this.optionNames.map((optionName, optionIndex) => {
        const selectedInput = window.HFDS.query(
          `${INPUT_SELECTOR}` +
            `[data-option-index="${optionIndex}"]:checked`,
          this.root
        );

        return selectedInput?.value || "";
      });
    }

    /**
     * Finds an exact variant from selected option values.
     *
     * @param {string[]} selectedOptions
     * @returns {Object|null}
     */
    findExactVariant(selectedOptions) {
      return (
        this.variants.find((variant) => {
          if (!Array.isArray(variant.options)) {
            return false;
          }

          return variant.options.every(
            (value, index) =>
              String(value) ===
              String(selectedOptions[index] || "")
          );
        }) || null
      );
    }

    /**
     * Gets product form.
     *
     * @returns {HTMLFormElement|null}
     */
    getProductForm() {
      if (!this.formId) {
        return null;
      }

      const form = document.getElementById(this.formId);

      return form instanceof HTMLFormElement ? form : null;
    }

    /**
     * Gets or creates the hidden Shopify variant input.
     *
     * @returns {HTMLInputElement|null}
     */
    getVariantIdInput() {
      const form = this.getProductForm();

      if (!form) {
        return null;
      }

      let variantInput = form.querySelector(
        'input[name="id"]'
      );

      if (!(variantInput instanceof HTMLInputElement)) {
        variantInput = document.createElement("input");
        variantInput.type = "hidden";
        variantInput.name = "id";
        form.appendChild(variantInput);
      }

      return variantInput;
    }

    /**
     * Handles option changes.
     *
     * @param {Event} event
     */
    handleChange(event) {
      const input = event.target.closest(INPUT_SELECTOR);

      if (!(input instanceof HTMLInputElement)) {
        return;
      }

      this.resolveVariant({
        updateUrl: this.updateUrl,
        emitEvent: true,
        announce: true
      });
    }

    /**
     * Resolves and publishes the current variant.
     *
     * @param {Object} options
     */
    resolveVariant({
      updateUrl = true,
      emitEvent = true,
      announce = true
    } = {}) {
      this.root.setAttribute("aria-busy", "true");

      const selectedOptions = this.getSelectedOptions();
      const variant = this.findExactVariant(selectedOptions);

      this.currentVariant = variant;

      this.updateSelectionLabels();
      this.updateOptionAvailability(selectedOptions);
      this.updateVariantInput(variant);
      this.updateStatus(variant);

      if (variant && updateUrl) {
        this.updateVariantUrl(variant.id);
      }

      if (emitEvent) {
        this.emitVariantEvent(variant, selectedOptions);
      }

      if (announce) {
        this.announceVariant(variant);
      }

      this.root.setAttribute("aria-busy", "false");
    }

    /**
     * Updates visible selected-value labels.
     */
    updateSelectionLabels() {
      window.HFDS
        .queryAll(SELECTION_SELECTOR, this.root)
        .forEach((label) => {
          const optionIndex = Number(label.dataset.optionIndex);
          const input = window.HFDS.query(
            `${INPUT_SELECTOR}` +
              `[data-option-index="${optionIndex}"]:checked`,
            this.root
          );

          label.textContent = input?.value || "";
        });
    }

    /**
     * Returns whether an option value appears in any real variant.
     *
     * @param {number} optionIndex
     * @param {string} value
     * @returns {boolean}
     */
    valueExists(optionIndex, value) {
      return this.variants.some((variant) => {
        return (
          Array.isArray(variant.options) &&
          String(variant.options[optionIndex]) === String(value)
        );
      });
    }

    /**
     * Returns whether an option value can produce an available variant,
     * while respecting selections made in the other option groups.
     *
     * @param {number} optionIndex
     * @param {string} value
     * @param {string[]} selectedOptions
     * @returns {boolean}
     */
    valueHasAvailableCombination(
      optionIndex,
      value,
      selectedOptions
    ) {
      return this.variants.some((variant) => {
        if (!variant.available || !Array.isArray(variant.options)) {
          return false;
        }

        if (
          String(variant.options[optionIndex]) !== String(value)
        ) {
          return false;
        }

        return selectedOptions.every(
          (selectedValue, selectedIndex) => {
            if (selectedIndex === optionIndex) {
              return true;
            }

            if (!selectedValue) {
              return true;
            }

            return (
              String(variant.options[selectedIndex]) ===
              String(selectedValue)
            );
          }
        );
      });
    }

    /**
     * Updates unavailable states for every option.
     *
     * @param {string[]} selectedOptions
     */
    updateOptionAvailability(selectedOptions) {
      this.getInputs().forEach((input) => {
        const optionIndex = Number(input.dataset.optionIndex);
        const value = input.value;
        const pill = this.root.querySelector(
          `[data-hf-option-pill="${CSS.escape(input.id)}"]`
        );

        if (!(pill instanceof HTMLElement)) {
          return;
        }

        const exists = this.valueExists(optionIndex, value);
        const hasAvailableCombination =
          this.valueHasAvailableCombination(
            optionIndex,
            value,
            selectedOptions
          );

        pill.classList.toggle(
          "is-unavailable",
          exists && !hasAvailableCombination
        );

        pill.dataset.available = String(
          hasAvailableCombination
        );

        if (this.disableImpossible) {
          input.disabled = !exists;
        } else {
          input.disabled = false;
        }

        if (!hasAvailableCombination) {
          pill.setAttribute(
            "aria-description",
            `${value} is unavailable with the current selection`
          );
        } else {
          pill.removeAttribute("aria-description");
        }
      });
    }

    /**
     * Synchronizes Shopify's hidden variant ID.
     *
     * @param {Object|null} variant
     */
    updateVariantInput(variant) {
      const variantInput = this.getVariantIdInput();

      if (!variantInput) {
        return;
      }

      variantInput.value = variant ? String(variant.id) : "";

      variantInput.dispatchEvent(
        new Event("change", {
          bubbles: true
        })
      );
    }

    /**
     * Updates visible status.
     *
     * @param {Object|null} variant
     */
    updateStatus(variant) {
      const status = window.HFDS.query(
        STATUS_SELECTOR,
        this.root
      );

      if (!status) {
        return;
      }

      status.classList.remove(
        "is-success",
        "is-warning",
        "is-danger"
      );

      if (!variant) {
        status.textContent =
          "This combination is not available.";
        status.classList.add("is-danger");
        return;
      }

      if (!variant.available) {
        status.textContent =
          "Currently unavailable in this selection.";
        status.classList.add("is-danger");
        return;
      }

      if (
        typeof variant.inventory_quantity === "number" &&
        variant.inventory_quantity > 0 &&
        variant.inventory_quantity <= 5
      ) {
        status.textContent =
          `Low stock — ${variant.inventory_quantity} remaining.`;
        status.classList.add("is-warning");
        return;
      }

      status.textContent = "In stock and ready to order.";
      status.classList.add("is-success");
    }

    /**
     * Updates the product URL without reloading.
     *
     * @param {string|number} variantId
     */
    updateVariantUrl(variantId) {
      if (!variantId || !window.history?.replaceState) {
        return;
      }

      const url = new URL(window.location.href);
      url.searchParams.set("variant", String(variantId));

      window.history.replaceState(
        {
          ...window.history.state,
          hfVariantId: String(variantId)
        },
        "",
        url.toString()
      );
    }

    /**
     * Emits the central HFDS variant event.
     *
     * @param {Object|null} variant
     * @param {string[]} selectedOptions
     */
    emitVariantEvent(variant, selectedOptions) {
      window.HFDS.emit(
        "variant:change",
        {
          productId: this.productId,
          sectionId: this.sectionId,
          formId: this.formId,
          variant,
          selectedOptions,
          available: Boolean(variant?.available)
        },
        this.root
      );
    }

    /**
     * Announces selection to assistive technology.
     *
     * @param {Object|null} variant
     */
    announceVariant(variant) {
      if (!variant) {
        window.HFDS.announce(
          "This variant combination is unavailable.",
          "polite"
        );
        return;
      }

      const price = window.HFDS.formatMoney(
        variant.price,
        window.Shopify?.currency?.active || "USD"
      );

      const availability = variant.available
        ? "available"
        : "currently unavailable";

      window.HFDS.announce(
        `${variant.title}, ${price}, ${availability}.`,
        "polite"
      );
    }

    /**
     * Prevents a product form from submitting without a variant.
     *
     * @param {SubmitEvent} event
     */
    handleFormSubmit(event) {
      if (!this.currentVariant) {
        event.preventDefault();

        this.updateStatus(null);

        window.HFDS.announce(
          "Please choose an available product option.",
          "assertive"
        );

        const firstInput = this.getInputs()[0];

        if (firstInput) {
          firstInput.focus();
        }

        return;
      }

      if (!this.currentVariant.available) {
        event.preventDefault();

        window.HFDS.announce(
          "The selected variant is currently unavailable.",
          "assertive"
        );
      }
    }

    /**
     * Shows a fatal component error.
     *
     * @param {string} message
     */
    setError(message) {
      this.root.classList.add("has-error");
      this.root.removeAttribute("aria-busy");

      const status = window.HFDS.query(
        STATUS_SELECTOR,
        this.root
      );

      if (status) {
        status.textContent = message;
        status.classList.add("is-danger");
      }

      console.error(
        `[HFDS Variant Picker] ${message}`
      );
    }

    /**
     * Cleanup for Shopify Theme Editor reloads.
     */
    destroy() {
      this.root.removeEventListener(
        "change",
        this.handleChange
      );

      const form = this.getProductForm();

      if (form) {
        form.removeEventListener(
          "submit",
          this.handleFormSubmit
        );
      }

      delete this.root.dataset.hfInitialized;
    }
  }

  /**
   * Initializes all uninitialized pickers.
   *
   * @param {ParentNode} context
   */
  function initializeVariantPickers(context = document) {
    if (!window.HFDS) {
      return;
    }

    window.HFDS
      .queryAll(ROOT_SELECTOR, context)
      .forEach((root) => {
        if (!(root instanceof HTMLElement)) {
          return;
        }

        if (root.dataset.hfInitialized === "true") {
          return;
        }

        root.hfVariantPicker = new HFVariantPicker(root);
      });
  }

  window.HFDS?.ready(() => {
    initializeVariantPickers(document);
  });

  document.addEventListener(
    "shopify:section:load",
    (event) => {
      initializeVariantPickers(event.target);
    }
  );

  document.addEventListener(
    "shopify:section:unload",
    (event) => {
      window.HFDS
        ?.queryAll(ROOT_SELECTOR, event.target)
        .forEach((root) => {
          root.hfVariantPicker?.destroy();
        });
    }
  );

  window.HFDSVariantPicker = Object.freeze({
    version: "1.0.0",
    initialize: initializeVariantPickers
  });
})();